import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { Badge, Button, Combobox, Field, IconClose } from '@/design-system';
import { nonconformitiesApi, queryKeys } from '@/lib/api';

/**
 * SELETOR DE NÃO-CONFORMIDADE COM BUSCA NO SERVIDOR.
 *
 * É a contraparte de `searchNC`/`getNcSearch` do legado
 * (`controller/qualidade/qualidade.php:254`, `model/qualidade/qualidade.php:49-62`):
 * `LIKE` no título OU no número, restrito à empresa, `LIMIT 20`. A auditoria da
 * Onda 2 registrou a ausência em A28 — não havia busca de NC de origem em tela
 * nenhuma, e por isso o campo `recurrenceOfIds` do contrato nunca recebia nada
 * além de `[]` literal.
 *
 * POR QUE NÃO REUSAR `PeoplePicker`: ele filtra em memória a lista que recebe, e
 * o diretório cabe em memória — o catálogo de NCs de uma organização com anos de
 * uso, não. Aqui a busca vai ao servidor a cada digitação, com
 * `keepPreviousData` para a lista não piscar.
 */

export interface NonconformityPickerProps {
  readonly selectedIds: readonly string[];
  readonly onSelectedIdsChange: (ids: string[]) => void;
  /** NC que não pode ser escolhida — tipicamente a própria (§A.4, auto-relação). */
  readonly excludeId?: string | undefined;
  readonly label: string;
  readonly description?: string;
  readonly error?: string | undefined;
  readonly disabled?: boolean;
  readonly max?: number;
}

const PAGE_SIZE = 20;

export function NonconformityPicker({
  selectedIds,
  onSelectedIdsChange,
  excludeId,
  label,
  description,
  error,
  disabled = false,
  max = 50,
}: NonconformityPickerProps) {
  const [search, setSearch] = useState('');

  const filter = useMemo(
    () => ({
      page: 1,
      // O `LIMIT 20` do legado, preservado: a busca é para reconhecer, não para
      // navegar o acervo inteiro.
      pageSize: PAGE_SIZE,
      scope: 'ALL' as const,
      ...(search.trim() ? { search: search.trim() } : {}),
    }),
    [search],
  );

  const listQuery = useQuery({
    queryKey: queryKeys.nonconformities.list(filter),
    queryFn: () => nonconformitiesApi.list(filter),
    placeholderData: keepPreviousData,
  });

  /**
   * Rótulos acumulados das NCs já vistas na busca.
   *
   * A ficha da NC escolhida precisa continuar mostrando número e título depois
   * que o usuário limpa o termo digitado — e a página seguinte da busca não
   * traz mais a NC anterior. Um cache local resolve sem uma segunda consulta:
   * o dado só entra aqui porque JÁ passou pela lista, então nada é exibido que
   * o servidor não tenha autorizado a ver.
   */
  const [labels, setLabels] = useState<
    Readonly<Record<string, { code: string; title: string; statusLabel: string }>>
  >({});

  useEffect(() => {
    const items = listQuery.data?.items;
    if (!items?.length) return;
    setLabels((current) => {
      let changed = false;
      const next = { ...current };
      for (const item of items) {
        if (next[item.id]) continue;
        next[item.id] = {
          code: item.code,
          title: item.title,
          statusLabel: item.statusLabel,
        };
        changed = true;
      }
      return changed ? next : current;
    });
  }, [listQuery.data]);

  const known = useMemo(() => new Map(Object.entries(labels)), [labels]);

  const options = useMemo(
    () =>
      (listQuery.data?.items ?? [])
        .filter((item) => item.id !== excludeId && !selectedIds.includes(item.id))
        .map((item) => ({
          value: item.id,
          label: `${item.code} — ${item.title}`,
          hint: item.statusLabel,
        })),
    [listQuery.data, excludeId, selectedIds],
  );

  const atLimit = selectedIds.length >= max;

  return (
    <Field label={label} description={description} error={error}>
      <div className="flex flex-col gap-2">
        <Combobox
          items={options}
          value={null}
          onValueChange={(value) => {
            if (!value || atLimit) return;
            onSelectedIdsChange([...selectedIds, value]);
            setSearch('');
          }}
          onSearchChange={setSearch}
          loading={listQuery.isPending}
          disabled={disabled || atLimit}
          placeholder={
            atLimit ? `Limite de ${max} registros atingido` : 'Busque por número ou título'
          }
          emptyMessage="Nenhuma não-conformidade encontrada."
          ariaLabel={label}
        />

        {selectedIds.length === 0 ? (
          <p className="text-xs text-content-subtle">
            Nenhuma não-conformidade de origem selecionada.
          </p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {selectedIds.map((id) => {
              const item = known.get(id);
              return (
                <li
                  key={id}
                  className="flex flex-wrap items-center gap-2 rounded-md border border-border-subtle bg-surface-sunken px-2.5 py-1.5"
                >
                  <span className="font-mono text-xs font-medium text-brand">
                    {item?.code ?? '—'}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-content">
                    {item?.title ?? 'Não-conformidade não encontrada'}
                  </span>
                  {item?.statusLabel ? (
                    <Badge tone="neutral" size="sm" srPrefix="Situação:">
                      {item.statusLabel}
                    </Badge>
                  ) : null}
                  {/* `h-8 w-8` mantém a altura que a linha já tinha; o padrão
                      do tamanho `icon` (2.375rem) esticaria cada ficha. */}
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={disabled}
                    iconLabel={`Remover ${item?.code ?? 'registro'} da lista`}
                    onClick={() =>
                      onSelectedIdsChange(selectedIds.filter((value) => value !== id))
                    }
                    className="h-8 w-8"
                  >
                    <IconClose size={14} />
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Field>
  );
}
